import { useEffect, useMemo } from "react";
import { useGetActuarialUserListQuery, useGetComputationForApprovalListQuery } from "../../api/actuarial/actuarial-api"
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { DropdownOptionValue } from "../../components/ui/dropdown-option";
import { useDispatch, useSelector } from "react-redux";
import { ActuarialDepartmentListResponseData } from "../../api/actuarial/actuarial-api-response";
import { selectApproverFormRates } from "../../features/actuarial/approval/approval-slice";
import { PaginationType, selectAllApprovalPagination, selectApprovalPagination, setPagination } from "../../features/pagination/pagination-slice";
import { options } from "../../data/group-insurance";
import { GetProposalParams } from "../../api/gms/proposal/proposal-api-request";
import { SearchField } from "../../types/type";
import { DropdownOption } from "../../features/auth/auth-state";
import { Option } from "../../features/it-user-forms/user-form-state";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { totalPages, TotalPages } from "../../features/pagination/pagination-inital-state";

type ApprovalQueueMode = "my" | "team";

interface Props {
    mode: ApprovalQueueMode;
    canFetch?: boolean;
}

export const useProposalAllComputationAccess = ({ mode, canFetch }: Props) => {

    const dispatch = useDispatch();

    const includedOptionIds = [1, 3];
    const normalizedOptionsData: Option[] = options.filter((option) => includedOptionIds.includes(option.id)).map((option) => ({  
        id: option.id, 
        value: option.value,
        name: option.name 
    }));
    const normalizedOptions: DropdownOption[] = options.filter((option) => includedOptionIds.includes(option.id)).map((option) => ({ 
        id: option.id, 
        name: option.name 
    }));

    const { 
        data: actuarialUsers, 
        isLoading: isLoadingActuarialUsers, 
        isError: isErrorActuarialUsers, 
        error: errorActuarialUsers, 
        refetch: refetchActuarialUsers, 
        isFetching: isFetchingActuarialUsers 
    } = useGetActuarialUserListQuery(undefined, {
        skip: !canFetch,
    });
    const errorMessageActuarialUsers = getErrorMessage(errorActuarialUsers);

    const approverForm = useSelector(selectApproverFormRates);

    const defaultActuarialListType = useMemo(() => ({ 
        id: 0, name: "Select Approver", 
        subTitle: "-" 
    }), []);

    const actuarialTypeOptions: DropdownOptionValue[] = useMemo(() => [
        defaultActuarialListType,
        ...(actuarialUsers?.map((item: ActuarialDepartmentListResponseData) => ({ 
            id: item.user_id, 
            name: `${item.firstname} ${item.middlename} ${item.lastname}`, 
            subTitle: item.position ?? "" 
        })) ?? []),
    ], [defaultActuarialListType, actuarialUsers]);

    // COUNT BADGE
    const totalParams: GetProposalParams & { my?: boolean } = { page: 1, per_page: 1 };
    if (mode === "my") totalParams.my = true;

    const { data: approvalComputationTotal } = useGetComputationForApprovalListQuery(totalParams, {skip: !canFetch});

    const selectedApproverValue = useMemo(() => actuarialTypeOptions.find((option) => option.id === approverForm?.id) ?? defaultActuarialListType, [approverForm?.id, actuarialTypeOptions, defaultActuarialListType]);

    const pagination = useSelector(mode === "my" ? selectApprovalPagination : selectAllApprovalPagination);
    const paginationType: PaginationType = mode === "my" ? "approvalProposal" : "allApprovalProposal";
    const { 
        searchType, 
        searchValue, 
        page, 
        defaultTotalPages, 
        isTyping, 
        isPageSizeChanging, 
        isDebounceDone 
    } = pagination;
    const selectedSearchType = searchType ? { id: searchType.id, name: searchType.name } : null;

    const setSelectedSearchType = (type: { id: number; name: string } | null) => {
        if (!type) {
            dispatch(setPagination({ type: paginationType, value: { searchType: null, searchValue: "", page: 1 } }));
            return;
        }

        const option = normalizedOptionsData.find((option) => option.id === type.id) ?? null;
        dispatch(setPagination({ type: paginationType, value: { searchType: option, searchValue: "", page: 1 } }));
    };

    const setSearchValue = (value: string) => {
        dispatch(setPagination({ type: paginationType, value: { searchValue: value } }));
    };

    const setPage = (value: number) => {
        dispatch(setPagination({ type: paginationType, value: { page: value } }));
    };
    const { isActuarialDepartment, isMarketingGMSDepartment } = useProposalTypeData()
    // const canFetch = isActuarialDepartment || isMarketingGMSDepartment
    const params: GetProposalParams & { my?: boolean } = { 
        page, 
        per_page: defaultTotalPages.value 
    };
    if (mode === "my") params.my = true;
    if (searchType && searchValue.trim()) params[searchType.value as SearchField] = searchValue.trim();
    const { 
        data: approvalComputationList, 
        isLoading: isLoadingApprovalComputationList, 
        isError: isErrorApprovalComputationList, 
        error: errorApprovalComputationList, 
        refetch: refetchApprovalComputationList, 
        isFetching: isFetchingApprovalComputationList 
    } = useGetComputationForApprovalListQuery(params, {
    skip: !canFetch,
});
    const errorMessageApprovalComputationList = getErrorMessage(errorApprovalComputationList);

    useEffect(() => {
        dispatch(setPagination({ type: paginationType, value: { isTyping: true, isDebounceDone: false } }));

        const timer = setTimeout(() => {
            dispatch(setPagination({ type: paginationType, value: { isDebounceDone: true } }));
        }, 300);

        return () => clearTimeout(timer);
    }, [searchValue, dispatch, paginationType]);

    useEffect(() => {
        if (isDebounceDone && !isFetchingApprovalComputationList) {
            dispatch(setPagination({ type: paginationType, value: { isTyping: false } }));
        }
    }, [isDebounceDone, isFetchingApprovalComputationList, dispatch, paginationType]);

    useEffect(() => {
        if (isPageSizeChanging && !isFetchingApprovalComputationList) {
            dispatch(setPagination({ type: paginationType, value: { isPageSizeChanging: false } }));
        }
    }, [isPageSizeChanging, isFetchingApprovalComputationList, dispatch, paginationType]);

    const handleChangePageSize = (value: TotalPages) => {
        dispatch(setPagination({ 
            type: paginationType, 
            value: { 
                defaultTotalPages: value, 
                page: 1, 
                isPageSizeChanging: true 
            } 
        }));
    };

    const handleReload = () => {
        dispatch(setPagination({ type: paginationType, value: { page: 1 } }));
        refetchApprovalComputationList();
    };

    const loading = isTyping || isLoadingApprovalComputationList || isPageSizeChanging;

    return {
        approvalTotal: approvalComputationTotal?.pagination?.total ?? 0,
        mode,
        approvalComputationListResponse: approvalComputationList,
        approvalComputationList: approvalComputationList?.data ?? [],
        pagedData: approvalComputationList?.data ?? [],
        approvalPagination: approvalComputationList?.pagination,
        setPage,
        handleReload,
        defaultTotalPages,
        handleChangePageSize,
        paginationDefaultPageSize: totalPages,
        loading,
        normalizedOptions,
        selectedSearchType,
        searchType: selectedSearchType,
        searchValue,
        setSelectedSearchType,
        setSearchType: setSelectedSearchType,
        setSearchValue,
        isLoadingApprovalComputationList,
        isErrorApprovalComputationList,
        errorApprovalComputationList,
        refetchApprovalComputationList,
        isFetchingApprovalComputationList,
        errorMessageApprovalComputationList,
        actuarialUsers,
        isLoadingActuarialUsers,
        isErrorActuarialUsers,
        errorActuarialUsers,
        refetchActuarialUsers,
        isFetchingActuarialUsers,
        errorMessageActuarialUsers,
        actuarialTypeOptions,
        selectedApproverValue,
    };
}